import React from 'react'
import { useTranslation } from 'react-i18next'
import { Cookie, ExternalLink, Lock, Pencil, ShieldCheck } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useRoute } from '@/lib/RouteContext'
import { useSidebar } from '@/lib/SidebarContext'
import { useMenu } from '@/lib/MenuContext'
import { useAuth } from '@/lib/AuthContext'
import { useOptionalCommunity } from '@/lib/CommunityContext'
import { useMenuCounters } from '@/lib/menuCounters'
import { isSafeExternalUrl } from '@/lib/safeUrl'
import { lockReasonKey } from '@/types/access'
import { type Section } from '@/types/sections'
import { type Space } from '@/types/spaces'
import { moduleIcon } from '@/modules/registry'
import { AppLink } from '@/components/ui/app-link'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useChannelUnread, useChannelUnreadAll } from '@/components/features/channel/channelUnread'
import { useHasCookieChoice } from '@/components/features/cookies/useHasCookieChoice'
import { openCookieNotice } from '@/lib/cookieConsent'
import { getIconComponent, isRouteActive, useAdminItems } from './Sidebar'
import { MentionBadge, NewsBadge } from './menuBadges'
import { usePersonalLinks } from './PersonalLinks'
import { LanguageTag } from '@/components/features/spaces/LanguageTag'

/**
 * F-191 phase 2: the compact side menu (from 1024 px). Each section is an icon
 * with a tooltip; a click opens a floating menu with its spaces (a section
 * with one space goes straight to it). Administration is one more icon with
 * its own floating menu. Reordering stays in the full menu: "Editar menu"
 * switches back to it. The full `Sidebar` stays mounted (hidden) beside it,
 * so the channel and news counters keep syncing from there.
 */

interface TooltipState { label: string; top: number }

/** One tooltip for the whole rail: the nav scrolls, and a scroller would clip a tooltip placed inside it. */
const useRailTooltip = () => {
  const [tip, setTip] = React.useState<TooltipState | null>(null)
  const bind = (label: string) => {
    const show = (event: React.SyntheticEvent<HTMLElement>) => {
      const rect = event.currentTarget.getBoundingClientRect()
      setTip({ label, top: rect.top + rect.height / 2 })
    }
    const hide = () => setTip(null)
    return { onMouseEnter: show, onFocus: show, onMouseLeave: hide, onBlur: hide }
  }
  return { tip, bind, hide: () => setTip(null) }
}

type TooltipBind = ReturnType<typeof useRailTooltip>['bind']

const railItemClass = (active: boolean) => cn(
  'relative flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl transition-colors',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
  active ? 'bg-primary-50 text-primary' : 'text-foreground-secondary hover:bg-muted'
)

/** The number on an icon: unread mentions win (red), then news; a channel with only unread messages gets a dot. */
export const RailBadge = ({ mentions, news, unread }: { mentions: number; news: number; unread: boolean }) => {
  if (mentions > 0) return <MentionBadge count={mentions} className="absolute -right-1 -top-1 h-4 min-w-[1rem] px-1 text-[10px]" />
  if (news > 0) return <NewsBadge count={news} className="absolute -right-1 -top-1 h-4 min-w-[1rem] px-1 text-[10px]" />
  if (unread) return <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-primary" data-channel-unread="true" aria-hidden="true" />
  return null
}

/** A section's initials (1-2 letters): "AI Hub" -> "AH", "Resources" -> "R". */
const initialsOf = (title: string) => {
  const words = title.trim().split(/\s+/).filter(Boolean)
  const letters = words.length > 1 ? [words[0], words[1]].map((word) => Array.from(word)[0]) : Array.from(words[0] ?? '?').slice(0, 1)
  return letters.join('').toUpperCase()
}

/**
 * The section's own icon; with none (or the default folder every section gets),
 * a monogram tile in the community color, so sections differ at a glance.
 */
export const SectionGlyph = ({ section, active }: { section: Section; active: boolean }) => {
  if (section.icon && section.icon !== 'folder') {
    const Icon = getIconComponent(section.icon)
    return <Icon className="h-5 w-5" />
  }
  return (
    <span
      aria-hidden="true"
      data-rail-monogram
      className={cn('flex h-8 w-8 items-center justify-center rounded-lg text-xs font-semibold tracking-tight',
        active ? 'bg-primary text-white' : 'bg-primary-100 text-primary-700')}
    >
      {initialsOf(section.title)}
    </span>
  )
}

export const spaceIconOf = (space: Space) => (space.icon ? getIconComponent(space.icon) : moduleIcon(space.module))

/** A space row inside a section's floating menu: icon, name, its badge and the padlock. */
export const RailSpaceItem = ({ space, section, active }: { space: Space; section: Section; active: boolean }) => {
  const { t } = useTranslation(['navigation', 'common'])
  const Icon = spaceIconOf(space)
  const channel = useChannelUnread(space.module === 'channel' ? space.id : undefined)
  const mentions = channel?.mentions ?? 0
  const news = useMenuCounters().spaces[space.id] ?? 0
  const lockKey = space.locked ? lockReasonKey(space.lockReason) ?? 'common:access.lockReason.product' : null
  const content = (
    <>
      <Icon className="h-4 w-4 flex-shrink-0" />
      <span className={cn('min-w-0 flex-1 truncate', channel?.hasUnread && 'font-semibold text-foreground')}>{space.title}</span>
      <LanguageTag languages={space.languages} />
      {mentions > 0 ? <MentionBadge count={mentions} /> : news > 0 && !active ? <NewsBadge count={news} /> : null}
      {space.module === 'url' && !space.locked && <ExternalLink className="h-3 w-3 flex-shrink-0" />}
      {lockKey && <Lock className="h-3 w-3 flex-shrink-0 text-[rgb(var(--rgb-gray-400))]" aria-label={t(lockKey)} />}
    </>
  )
  const itemClass = cn('gap-2', active && 'bg-primary-50 text-primary')
  if (space.module === 'url' && !space.locked) {
    const external = space.externalUrl && isSafeExternalUrl(space.externalUrl) ? String(space.externalUrl).trim() : null
    if (!external) return <DropdownMenuItem disabled className={itemClass}>{content}</DropdownMenuItem>
    return (
      <DropdownMenuItem asChild className={itemClass}>
        <a href={external} target="_blank" rel="noopener noreferrer">{content}</a>
      </DropdownMenuItem>
    )
  }
  return (
    <DropdownMenuItem asChild className={itemClass}>
      <AppLink to={`/${section.slug}/${space.slug}`} aria-current={active ? 'page' : undefined}>{content}</AppLink>
    </DropdownMenuItem>
  )
}

const RailSection = ({ section, activeSpaceId, bind, onOpenMenu }: {
  section: Section
  activeSpaceId: string | null
  bind: TooltipBind
  onOpenMenu: () => void
}) => {
  const { t } = useTranslation(['navigation', 'common'])
  const spaces = section.spaces ?? []
  const active = useSectionActive(section, activeSpaceId)
  const { mentions, unread, news } = useSectionCounters(section)
  const sectionLockKey = section.locked ? lockReasonKey(section.lockReason) : null
  const label = sectionLockKey ? `${section.title} (${t(sectionLockKey)})` : section.title
  const badge = <RailBadge mentions={mentions} news={news} unread={unread} />

  /* A padlocked section with nothing sent (F-161a) or an empty one: the icon opens the section page. */
  if (spaces.length === 0) {
    return (
      <AppLink to={`/${section.slug}`} aria-label={label} aria-current={active ? 'page' : undefined} className={railItemClass(active)} data-rail-section={section.id} {...bind(label)}>
        <SectionGlyph section={section} active={active} />
        {sectionLockKey && <Lock className="absolute bottom-1 right-1 h-3 w-3 text-[rgb(var(--rgb-gray-400))]" aria-hidden="true" />}
      </AppLink>
    )
  }

  /* One space: the icon is that space (P2 criterion 2). */
  if (spaces.length === 1) {
    const [space] = spaces
    const spaceLabel = `${section.title}: ${space.title}`
    if (space.module === 'url' && !space.locked) {
      const external = space.externalUrl && isSafeExternalUrl(space.externalUrl) ? String(space.externalUrl).trim() : null
      return (
        <a href={external ?? undefined} target="_blank" rel="noopener noreferrer" aria-label={spaceLabel} className={railItemClass(false)} data-rail-section={section.id} {...bind(spaceLabel)}>
          <SectionGlyph section={section} active={active} />
        </a>
      )
    }
    return (
      <AppLink to={`/${section.slug}/${space.slug}`} aria-label={spaceLabel} aria-current={active ? 'page' : undefined} className={railItemClass(active)} data-rail-section={section.id} {...bind(spaceLabel)}>
        <SectionGlyph section={section} active={active} />
        {badge}
      </AppLink>
    )
  }

  return (
    <DropdownMenu onOpenChange={(open) => { if (open) onOpenMenu() }}>
      <DropdownMenuTrigger asChild>
        <button type="button" aria-label={label} className={railItemClass(active)} data-rail-section={section.id} {...bind(label)}>
          <SectionGlyph section={section} active={active} />
          {badge}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="right" align="start" sideOffset={10} className="max-h-[min(70vh,32rem)] w-64 overflow-y-auto" data-rail-menu={section.id}>
        <DropdownMenuLabel className="truncate text-xs font-semibold uppercase tracking-wider text-muted-foreground">{section.title}</DropdownMenuLabel>
        {spaces.map((space) => (
          <RailSpaceItem key={space.id} space={space} section={section} active={space.id === activeSpaceId} />
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/** Sections in menu order: Home first, then the API's order (a copy, never the context's own array). */
export const useOrderedSections = () => {
  const { sections } = useMenu()
  return React.useMemo(
    () => [...sections].sort((a, b) => Number(!!b.isHome) - Number(!!a.isHome) || a.order - b.order),
    [sections]
  )
}

/** The space on screen: by its route, or the home space on `/`. */
export const useActiveSpaceId = () => {
  const { currentPath, params } = useRoute()
  const { sections, getHomeSpace } = useMenu()
  return React.useMemo(() => {
    if (currentPath === '/') return getHomeSpace()?.id ?? null
    const section = sections.find((item) => item.slug === params.sectionSlug)
    return section?.spaces?.find((space) => space.slug === params.spaceSlug)?.id ?? null
  }, [currentPath, getHomeSpace, sections, params.sectionSlug, params.spaceSlug])
}

/** A section's counters (phases 2 and 2b): unread mentions, news and whether a channel has unread messages. */
export const useSectionCounters = (section: Section) => {
  const counters = useMenuCounters()
  const channels = useChannelUnreadAll()
  const spaces = section.spaces ?? []
  return {
    mentions: spaces.reduce((sum, space) => sum + (channels[space.id]?.mentions ?? 0), 0),
    unread: spaces.some((space) => channels[space.id]?.hasUnread),
    news: counters.sections[section.id] ?? 0,
  }
}

/** Whether the route is inside this section (its page or one of its spaces). */
export const useSectionActive = (section: Section, activeSpaceId: string | null) => {
  const { currentPath } = useRoute()
  const routeSectionSlug = currentPath.split(/[?#]/)[0].split('/').filter(Boolean)[0]
  return routeSectionSlug === section.slug || (section.spaces ?? []).some((space) => space.id === activeSpaceId)
}

export const SidebarRail = () => {
  const { t } = useTranslation(['navigation', 'common'])
  const { isRail, setMemberMode } = useSidebar()
  const { currentPath } = useRoute()
  const { isAuthenticated, hasRole } = useAuth()
  const hasCookieChoice = useHasCookieChoice()
  const community = useOptionalCommunity()
  const adminItems = useAdminItems().filter((item) => item.visible)
  const personalLinks = usePersonalLinks()
  const { tip, bind, hide } = useRailTooltip()

  const ordered = useOrderedSections()
  const activeSpaceId = useActiveSpaceId()

  if (!isRail) return null

  const adminActive = adminItems.some((item) => isRouteActive(currentPath, item.path))
  const canEdit = hasRole(['owner', 'admin'])
  const adminLabel = t('administration')
  const editLabel = t('sidebar.editMenu')
  const cookiesLabel = t('common:cookies.link')
  const poweredLabel = t('entitlements:poweredBy')

  return (
    <aside
      className="theme-region-sidebar fixed left-0 top-16 z-40 hidden h-[calc(100dvh-4rem)] w-[--sidebar-rail-width] flex-col items-center border-r border-border bg-surface py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] lg:flex"
      aria-label={t('sidebar.railLabel')}
      data-sidebar-rail
    >
      <nav className="flex min-h-0 w-full flex-1 flex-col items-center gap-1 overflow-y-auto overflow-x-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" onScroll={hide}>
        {ordered.map((section) => (
          <RailSection key={section.id} section={section} activeSpaceId={activeSpaceId} bind={bind} onOpenMenu={hide} />
        ))}
        {canEdit && (
          <button type="button" aria-label={editLabel} className={cn(railItemClass(false), 'mt-1 text-[rgb(var(--rgb-gray-400))]')} onClick={() => { hide(); setMemberMode('full') }} data-rail-edit {...bind(editLabel)}>
            <Pencil className="h-4 w-4" />
          </button>
        )}
      </nav>

      {/* Meus ingressos, Minhas compras and Créditos, as in the full menu: pinned above Administração. */}
      {personalLinks.length > 0 && (
        <nav aria-label={t('personalMenu')} className="mt-2 flex w-full flex-shrink-0 flex-col items-center gap-1 border-t border-border pt-2" data-rail-personal>
          {personalLinks.map((link) => {
            const active = isRouteActive(currentPath.split('?')[0], link.path)
            return (
              <AppLink key={link.key} to={link.path} aria-label={link.label} aria-current={active ? 'page' : undefined} className={railItemClass(active)} data-rail-personal-item={link.key} {...bind(link.label)}>
                <link.icon className="h-5 w-5" />
              </AppLink>
            )
          })}
        </nav>
      )}

      {isAuthenticated && adminItems.length > 0 && (
        <div className="mt-2 flex w-full flex-col items-center border-t border-border pt-2">
          <DropdownMenu onOpenChange={(open) => { if (open) hide() }}>
            <DropdownMenuTrigger asChild>
              <button type="button" aria-label={adminLabel} className={railItemClass(adminActive)} data-rail-admin {...bind(adminLabel)}>
                <ShieldCheck className="h-5 w-5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="right" align="end" sideOffset={10} className="max-h-[min(80vh,36rem)] w-60 overflow-y-auto">
              <DropdownMenuLabel className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{adminLabel}</DropdownMenuLabel>
              {adminItems.map((item) => {
                const active = isRouteActive(currentPath, item.path)
                return (
                  <DropdownMenuItem key={item.path} asChild className={cn('gap-2', active && 'bg-primary-50 text-primary')}>
                    <AppLink to={item.path} aria-current={active ? 'page' : undefined}>
                      <item.icon className="h-4 w-4 flex-shrink-0" />
                      <span className="truncate">{item.label}</span>
                    </AppLink>
                  </DropdownMenuItem>
                )
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {/* F-201: reopens the cookie notice, as in the full menu. */}
      {hasCookieChoice && (
        <button type="button" aria-label={cookiesLabel} onClick={openCookieNotice} className={cn(railItemClass(false), 'mt-1 h-9 w-9 text-[rgb(var(--rgb-gray-400))]')} {...bind(cookiesLabel)}>
          <Cookie className="h-4 w-4" />
        </button>
      )}

      {/* F-34: the plan keeps the "Powered by Memberfy" mark (Starter); the API decides, as in the full menu. */}
      {community?.branding?.showMemberfyMark === true && (
        <a
          href="https://memberfy.net"
          target="_blank"
          rel="noopener noreferrer"
          aria-label={poweredLabel}
          className="mt-2 flex h-8 w-8 items-center justify-center rounded-lg opacity-70 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          data-testid="memberfy-mark"
          {...bind(poweredLabel)}
        >
          <img src="/favicon.svg" alt="" className="h-5 w-5" />
        </a>
      )}

      {tip && (
        <span
          role="tooltip"
          className="pointer-events-none fixed z-[60] -translate-y-1/2 whitespace-nowrap rounded-md bg-foreground px-2 py-1 text-xs font-medium text-surface shadow-lg"
          style={{ left: 'calc(var(--sidebar-rail-width) + 8px)', top: tip.top }}
        >
          {tip.label}
        </span>
      )}
    </aside>
  )
}
